import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Checkbox } from 'primereact/checkbox';
import { storage } from '../../../firebase/firebase.config';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { adminApi } from '../../../api/adminApi';

const CATEGORIAS = [
  { label: 'Manuales / Documentos', value: 'manuales' },
  { label: 'Placas', value: 'placas' },
  { label: 'Flyers', value: 'flyers' },
  { label: 'Información / Infografías', value: 'info' },
  { label: 'Otros', value: 'otros' },
];

const ESTADO_INICIAL = {
  titulo: '',
  descripcion: '',
  categoria: 'otros',
  orden: 100,
  archivo: null,
  thumbnail: null,
};

const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

function detectarTipo(file) {
  if (!file) return null;
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type?.startsWith('image/')) return 'image';
  return null;
}

function fmtSize(kb) {
  if (!kb) return '—';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function PanelDescargas() {
  const toast = useRef(null);
  const [descargas, setDescargas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(ESTADO_INICIAL);
  const [errores, setErrores] = useState({});
  const [archivoFile, setArchivoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (!archivoFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(archivoFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [archivoFile]);

  async function cargar() {
    setCargando(true);
    try {
      const res = await adminApi.getDescargas();
      setDescargas(res.data);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las descargas' });
    } finally {
      setCargando(false);
    }
  }

  const descargasFiltradas = descargas.filter((d) => {
    if (!mostrarInactivos && d.activo === false) return false;
    const q = filtro.toLowerCase();
    if (!q) return true;
    return (d.titulo || '').toLowerCase().includes(q) || (d.descripcion || '').toLowerCase().includes(q);
  });

  function abrirNueva() {
    setEditando('nueva');
    setForm(ESTADO_INICIAL);
    setArchivoFile(null);
    setErrores({});
  }

  function abrirEditar(d) {
    setEditando(d);
    setForm({
      titulo: d.titulo || '',
      descripcion: d.descripcion || '',
      categoria: d.categoria || 'otros',
      orden: d.orden ?? 100,
      archivo: d.archivo || null,
      thumbnail: d.thumbnail || null,
    });
    setArchivoFile(null);
    setErrores({});
  }

  function cerrarDialogo() {
    setEditando(null);
    setForm(ESTADO_INICIAL);
    setArchivoFile(null);
    setPreviewUrl(null);
    setErrores({});
  }

  function setCampo(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
    if (errores[k]) setErrores((p) => ({ ...p, [k]: undefined }));
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.current?.show({ severity: 'warn', summary: 'Archivo grande', detail: `Máximo ${MAX_BYTES / 1024 / 1024} MB` });
      return;
    }
    const tipo = detectarTipo(f);
    if (!tipo) {
      toast.current?.show({ severity: 'warn', summary: 'Tipo no permitido', detail: 'Solo PDF o imagen' });
      return;
    }
    setArchivoFile(f);
  }

  function validar() {
    const err = {};
    if (!form.titulo.trim()) err.titulo = 'Obligatorio';
    if (!form.categoria) err.categoria = 'Obligatorio';
    if (editando === 'nueva' && !archivoFile) err.archivo = 'Hay que subir un archivo';
    setErrores(err);
    return Object.keys(err).length === 0;
  }

  async function subirArchivoAStorage(file) {
    const tipo = detectarTipo(file);
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `descargas/${timestamp}_${safeName}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    const url = await getDownloadURL(ref);
    return {
      url,
      storagePath: path,
      nombreOriginal: file.name,
      tipo,
      sizeKb: Math.round(file.size / 1024),
    };
  }

  async function guardar() {
    if (!validar()) return;
    setGuardando(true);
    try {
      let archivoData = form.archivo;

      // Si hay un archivo nuevo seleccionado, lo subimos
      if (archivoFile) {
        setSubiendo(true);
        archivoData = await subirArchivoAStorage(archivoFile);
        setSubiendo(false);
      }

      const payload = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim(),
        categoria: form.categoria,
        orden: form.orden,
        archivo: archivoData,
      };

      if (editando === 'nueva') {
        await adminApi.crearDescarga(payload);
        toast.current?.show({ severity: 'success', summary: 'Descarga creada', detail: form.titulo });
      } else {
        await adminApi.editarDescarga(editando.id, payload);
        toast.current?.show({ severity: 'success', summary: 'Descarga actualizada', detail: form.titulo });
      }
      cerrarDialogo();
      await cargar();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detalles?.join(', ') || e?.message || 'No se pudo guardar';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg });
    } finally {
      setGuardando(false);
      setSubiendo(false);
    }
  }

  function confirmarDesactivar(d) {
    confirmDialog({
      message: (
        <div>
          ¿Desactivar la descarga <strong>"{d.titulo}"</strong>?
          <br />
          <small>El archivo en Storage se mantiene. Podés borrarlo también si querés.</small>
        </div>
      ),
      header: 'Confirmar desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Solo desactivar',
      rejectLabel: 'Desactivar y borrar archivo',
      accept: () => desactivar(d, false),
      reject: () => desactivar(d, true),
    });
  }

  async function desactivar(d, borrarArchivo) {
    try {
      const res = await adminApi.desactivarDescarga(d.id, borrarArchivo);
      const msg = res.data.archivoBorrado
        ? 'Descarga desactivada y archivo borrado de Storage'
        : 'Descarga desactivada';
      toast.current?.show({ severity: 'success', summary: 'Listo', detail: msg });
      await cargar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo desactivar' });
    }
  }

  async function reactivar(d) {
    try {
      await adminApi.reactivarDescarga(d.id);
      toast.current?.show({ severity: 'success', summary: 'Reactivada', detail: d.titulo });
      await cargar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo reactivar' });
    }
  }

  // ─── Column templates ─────────────────────────────────────────────────────
  const thumbTpl = (row) => {
    const url = row.thumbnail?.url || (row.archivo?.tipo === 'image' ? row.archivo?.url : null);
    if (url) {
      return <img src={url} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />;
    }
    return <i className="pi pi-file-pdf" style={{ fontSize: 36, color: '#d32f2f' }} />;
  };

  const tipoTpl = (row) => (
    <Tag value={(row.archivo?.tipo || '').toUpperCase()} severity={row.archivo?.tipo === 'pdf' ? 'danger' : 'info'} />
  );

  const tamanoTpl = (row) => fmtSize(row.archivo?.sizeKb);

  const categoriaTpl = (row) => {
    const c = CATEGORIAS.find((x) => x.value === row.categoria);
    return c?.label || row.categoria || '—';
  };

  const estadoTpl = (row) => (
    <Tag value={row.activo === false ? 'Inactiva' : 'Activa'} severity={row.activo === false ? 'secondary' : 'success'} />
  );

  const accionesTpl = (row) => (
    <div style={{ display: 'flex', gap: 4 }}>
      <Button icon="pi pi-pencil" rounded text severity="info" onClick={() => abrirEditar(row)} tooltip="Editar" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-external-link" rounded text severity="secondary" onClick={() => window.open(row.archivo?.url, '_blank')} tooltip="Ver archivo" tooltipOptions={{ position: 'top' }} />
      {row.activo === false
        ? <Button icon="pi pi-refresh" rounded text severity="success" onClick={() => reactivar(row)} tooltip="Reactivar" tooltipOptions={{ position: 'top' }} />
        : <Button icon="pi pi-ban" rounded text severity="danger" onClick={() => confirmarDesactivar(row)} tooltip="Desactivar" tooltipOptions={{ position: 'top' }} />
      }
    </div>
  );

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Descargas</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Checkbox inputId="inact" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.checked)} />
            <label htmlFor="inact" style={{ fontSize: 14, cursor: 'pointer' }}>Mostrar inactivas</label>
          </div>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar..." style={{ width: 240 }} />
          </span>
          <Button label="Nueva descarga" icon="pi pi-plus" onClick={abrirNueva} />
        </div>
      </div>

      <DataTable
        value={descargasFiltradas}
        loading={cargando}
        paginator
        rows={15}
        rowsPerPageOptions={[10, 15, 30, 50]}
        emptyMessage="No hay descargas registradas"
        stripedRows
        size="small"
      >
        <Column header="" body={thumbTpl} style={{ width: 70 }} />
        <Column field="titulo" header="Título" sortable style={{ minWidth: 200 }} />
        <Column header="Categoría" body={categoriaTpl} sortable field="categoria" style={{ minWidth: 150 }} />
        <Column header="Tipo" body={tipoTpl} sortable field="archivo.tipo" style={{ width: 100 }} />
        <Column header="Tamaño" body={tamanoTpl} style={{ width: 100 }} />
        <Column field="orden" header="Orden" sortable style={{ width: 90 }} />
        <Column field="descargas" header="↓" sortable style={{ width: 70 }} body={(r) => r.descargas || 0} />
        <Column header="Estado" body={estadoTpl} sortable field="activo" style={{ width: 110 }} />
        <Column header="Acciones" body={accionesTpl} style={{ width: 170 }} />
      </DataTable>

      <Dialog
        header={editando === 'nueva' ? 'Nueva descarga' : `Editar: ${editando?.titulo || ''}`}
        visible={!!editando}
        style={{ width: '90vw', maxWidth: 720 }}
        modal
        onHide={cerrarDialogo}
        footer={
          <div>
            <Button label="Cancelar" icon="pi pi-times" onClick={cerrarDialogo} text disabled={guardando} />
            <Button label={subiendo ? 'Subiendo…' : (guardando ? 'Guardando…' : 'Guardar')} icon="pi pi-check" onClick={guardar} disabled={guardando} />
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Campo label="Título *" error={errores.titulo}>
            <InputText value={form.titulo} onChange={(e) => setCampo('titulo', e.target.value)} className={errores.titulo ? 'p-invalid' : ''} />
          </Campo>
          <Campo label="Descripción">
            <InputTextarea value={form.descripcion} onChange={(e) => setCampo('descripcion', e.target.value)} rows={2} autoResize />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <Campo label="Categoría *" error={errores.categoria}>
              <Dropdown value={form.categoria} options={CATEGORIAS} optionLabel="label" optionValue="value" onChange={(e) => setCampo('categoria', e.value)} className={errores.categoria ? 'p-invalid' : ''} style={{ width: '100%' }} />
            </Campo>
            <Campo label="Orden (menor = primero)">
              <InputNumber value={form.orden} onValueChange={(e) => setCampo('orden', e.value ?? 100)} showButtons style={{ width: '100%' }} />
            </Campo>
          </div>

          <Campo label="Archivo (PDF o imagen, máx 20 MB)" error={errores.archivo}>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileChange}
              style={{ padding: 8, border: '1px solid #ced4da', borderRadius: 6, width: '100%' }}
            />
            {form.archivo && !archivoFile && (
              <small style={{ color: '#666', marginTop: 4 }}>
                Archivo actual: <strong>{form.archivo.nombreOriginal}</strong> ({fmtSize(form.archivo.sizeKb)}). Subí uno nuevo para reemplazarlo.
              </small>
            )}
          </Campo>

          {/* Preview */}
          {(previewUrl || form.archivo?.url) && (
            <div style={{ background: '#f5f7fa', padding: 12, borderRadius: 6, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Vista previa:</div>
              {(archivoFile && detectarTipo(archivoFile) === 'image') || (!archivoFile && form.archivo?.tipo === 'image')
                ? <img src={previewUrl || form.archivo.url} alt="preview" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 4 }} />
                : <div style={{ padding: 30 }}>
                    <i className="pi pi-file-pdf" style={{ fontSize: 64, color: '#d32f2f' }} />
                    <div style={{ marginTop: 8, fontSize: 13 }}>{archivoFile?.name || form.archivo?.nombreOriginal}</div>
                  </div>
              }
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

function Campo({ label, children, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{label}</label>
      {children}
      {error && <small style={{ color: '#c62828' }}>{error}</small>}
    </div>
  );
}
